'use client';

import { useState, useCallback } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  BatteryCharging,
  AlertTriangle,
  CheckCircle2,
  Zap,
  ShieldCheck,
  Clock,
  Mail,
  Phone,
  Building2,
  MapPin,
  MessageCircle,
  ArrowRight,
  RefreshCw,
} from 'lucide-react';
import {
  runDiagnosis,
  getSeverityStyles,
  diagnosticQuestions,
  type DiagnosticAnswers,
  type DiagnosticResult,
} from '@/lib/battery-diagnostic';
import { trackBatteryDiagnostic } from '@/lib/ad-tracking';

const steps = [
  { id: 1, label: 'Battery Type' },
  { id: 2, label: 'Symptoms' },
  { id: 3, label: 'Voltage' },
  { id: 4, label: 'Capacity' },
  { id: 5, label: 'Vehicle' },
];

export default function BatteryDiagnosticClient() {
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState<Partial<DiagnosticAnswers>>({
    symptoms: [],
  });
  const [result, setResult] = useState<DiagnosticResult | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Lead capture state
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadData, setLeadData] = useState({
    name: '',
    email: '',
    phone: '',
    whatsapp: '',
    company: '',
    country: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSingleSelect = useCallback((questionId: string, value: string) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  }, []);

  const handleMultiSelect = useCallback((questionId: string, value: string) => {
    setAnswers(prev => {
      const current = (prev as Record<string, string[]>)[questionId] || [];
      const next = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [questionId]: next };
    });
  }, []);

  const canProceed = useCallback(() => {
    switch (currentStep) {
      case 1:
        return !!answers.batteryType;
      case 2:
        return (answers.symptoms?.length || 0) > 0;
      case 3:
        return !!answers.voltageReading;
      case 4:
        return !!answers.capacityLoss;
      case 5:
        return true;
      default:
        return false;
    }
  }, [currentStep, answers]);

  const nextStep = useCallback(() => {
    if (currentStep < 5) {
      setCurrentStep(s => s + 1);
    } else {
      // Run diagnosis
      setIsAnalyzing(true);
      setTimeout(() => {
        const diag = runDiagnosis(answers as DiagnosticAnswers);
        setResult(diag);
        setShowResult(true);
        setIsAnalyzing(false);
      }, 1500);
    }
  }, [currentStep, answers]);

  const prevStep = useCallback(() => {
    if (showResult) {
      setShowResult(false);
      setCurrentStep(5);
    } else if (currentStep > 1) {
      setCurrentStep(s => s - 1);
    }
  }, [currentStep, showResult]);

  const restart = useCallback(() => {
    setCurrentStep(1);
    setAnswers({ symptoms: [] });
    setResult(null);
    setShowResult(false);
    setShowLeadForm(false);
    setSubmitted(false);
  }, []);

  const submitLead = useCallback(async () => {
    if (!leadData.email || !leadData.name) return;
    setSubmitting(true);
    try {
      const response = await fetch('/api/battery-diagnostic', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...leadData,
          battery_type: answers.batteryType,
          symptoms: answers.symptoms || [],
          voltage_reading: answers.voltageReading,
          capacity_loss: answers.capacityLoss,
          vehicle_brand: answers.vehicleBrand,
          vehicle_model: answers.vehicleModel,
          battery_capacity_kwh: answers.batteryCapacityKwh,
          mileage_km: answers.mileageKm,
          diagnosis_summary: result?.summary,
          severity: result?.severity,
          recommended_service: result?.recommendedService,
          estimated_cost_range: result?.estimatedCostRange,
          confidence_score: result?.confidenceScore,
          action_items: result?.actionItems || [],
        }),
      });
      if (response.ok && result) {
        const r = result;
        trackBatteryDiagnostic(r.recommendedService, r.severity, r.confidenceScore);
        setSubmitted(true);
      }
    } catch (e) {
      // Silent fail - result is still visible
      setSubmitted(true);
    }
    setSubmitting(false);
  }, [leadData, answers, result]);

  const getCurrentQuestion = () => {
    return diagnosticQuestions[currentStep - 1];
  };

  // Progress bar
  const progress = showResult ? 100 : ((currentStep - 1) / 4) * 100;

  if (isAnalyzing) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-6">
        <div className="relative">
          <div className="w-24 h-24 rounded-full border-4 border-[var(--color-border)] border-t-[var(--color-glow)] animate-spin" />
          <div className="absolute inset-0 flex items-center justify-center">
            <BatteryCharging className="w-10 h-10 text-[var(--color-primary)] animate-pulse" />
          </div>
        </div>
        <h2 className="mt-8 text-2xl font-bold text-[var(--color-foreground)]">
          Analyzing Your Battery...
        </h2>
        <p className="mt-3 text-[var(--color-muted)] text-center max-w-md">
          Our diagnostic engine is processing your responses to generate a comprehensive assessment.
        </p>
        <div className="mt-6 flex gap-2">
          {[0, 1, 2].map(i => (
            <div
              key={i}
              className="w-2 h-2 rounded-full bg-[var(--color-primary)] animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (showResult && result) {
    const styles = getSeverityStyles(result.severity);
    return (
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Result Header */}
        <div className={`${styles.bg} ${styles.border} border rounded-2xl p-8 mb-8 ${styles.glow}`}>
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <span className={`w-3 h-3 rounded-full ${styles.dot} animate-pulse`} />
                <span className={`text-sm font-semibold uppercase tracking-wider ${styles.text}`}>
                  {result.severityLabel}
                </span>
              </div>
              <h1 className="text-3xl font-bold text-[var(--color-foreground)] mb-2">
                {result.summary}
              </h1>
              <p className="text-[var(--color-muted)]">
                Confidence: <span className="text-[var(--color-foreground)] font-medium">{result.confidenceScore}%</span>
                {' · '}
                Based on {answers.symptoms?.length} symptom{(answers.symptoms?.length || 0) > 1 ? 's' : ''}
              </p>
            </div>
            <div className="text-right">
              <div className="text-sm text-[var(--color-muted)] mb-1">Est. Repair Cost</div>
              <div className="text-2xl font-bold text-[var(--color-primary)]">
                {result.estimatedCostRange}
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Analysis */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-[var(--color-foreground)] mb-4 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-[var(--color-primary)]" />
              Detailed Analysis
            </h3>
            <p className="text-[var(--color-muted)] leading-relaxed whitespace-pre-line">
              {result.detailedAnalysis}
            </p>
          </div>

          <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-2xl p-6">
            <h3 className="text-lg font-semibold text-[var(--color-foreground)] mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-[var(--color-primary)]" />
              Possible Causes
            </h3>
            <ul className="space-y-3">
              {result.possibleCauses.map((cause, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[var(--color-primary)] flex-shrink-0" />
                  <span className="text-[var(--color-muted)]">{cause}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Recommended Service */}
        <div className="bg-gradient-to-br from-[var(--color-surface)] to-[var(--color-surface-light)] border border-[var(--color-border)] rounded-2xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-[var(--color-primary)]/20 flex items-center justify-center">
              <ShieldCheck className="w-6 h-6 text-[var(--color-primary)]" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[var(--color-foreground)]">
                Recommended Service
              </h3>
              <p className="text-[var(--color-primary)] font-bold text-xl">
                {result.recommendedService}
              </p>
            </div>
          </div>

          <div className="mt-6 grid sm:grid-cols-2 gap-6">
            <div>
              <h4 className="text-sm font-semibold text-[var(--color-foreground)] mb-3 uppercase tracking-wide">
                What's Included
              </h4>
              <ul className="space-y-2">
                {result.actionItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[var(--color-muted)]">
                    <CheckCircle2 className="w-4 h-4 text-[var(--color-primary)] mt-0.5 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <h4 className="text-sm font-semibold text-[var(--color-foreground)] mb-3 uppercase tracking-wide">
                Benefits
              </h4>
              <ul className="space-y-2">
                {result.benefits.map((benefit, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-[var(--color-muted)]">
                    <Zap className="w-4 h-4 text-[var(--color-accent)] mt-0.5 flex-shrink-0" />
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Lead Capture or CTA */}
        {!showLeadForm && !submitted ? (
          <div className="text-center">
            <button
              onClick={() => setShowLeadForm(true)}
              className="px-8 py-4 bg-[var(--color-primary)] text-[var(--color-background)] font-bold rounded-xl hover:bg-[var(--color-primary-dark)] transition-all duration-300 shadow-[0_0_20px_rgba(0,255,136,0.3)] hover:shadow-[0_0_30px_rgba(0,255,136,0.5)] inline-flex items-center gap-2"
            >
              Get Full Diagnostic Report & Quote
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className="mt-3 text-sm text-[var(--color-muted)]">
              Free · No obligation · We'll contact you within 24 hours
            </p>
            <button
              onClick={restart}
              className="mt-6 flex items-center gap-2 text-[var(--color-muted)] hover:text-[var(--color-foreground)] transition-colors mx-auto"
            >
              <RefreshCw className="w-4 h-4" />
              Start New Diagnosis
            </button>
          </div>
        ) : submitted ? (
          <div className="text-center bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-8">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-8 h-8 text-emerald-400" />
            </div>
            <h3 className="text-2xl font-bold text-[var(--color-foreground)] mb-2">
              Report Sent!
            </h3>
            <p className="text-[var(--color-muted)] mb-6 max-w-md mx-auto">
              Thank you! We've received your diagnostic request. Our battery specialist will contact you within 24 hours with a detailed assessment and service quote.
            </p>
            <button
              onClick={restart}
              className="px-6 py-3 border border-[var(--color-border)] text-[var(--color-foreground)] rounded-xl hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors inline-flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Diagnose Another Battery
            </button>
          </div>
        ) : (
          <div className="bg-[var(--color-surface)] border border-[var(--color-primary)]/30 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-[var(--color-foreground)] mb-2">
              Get Your Full Diagnostic Report
            </h3>
            <p className="text-[var(--color-muted)] mb-6">
              Enter your details below and our battery specialist will send you a complete report with repair recommendations and exact pricing.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2">Name *</label>
                <input
                  type="text"
                  value={leadData.name}
                  onChange={e => setLeadData(d => ({ ...d, name: e.target.value }))}
                  className="w-full px-4 py-3 bg-[var(--color-background)] border border-[var(--color-border)] rounded-xl text-[var(--color-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2">Email *</label>
                <input
                  type="email"
                  value={leadData.email}
                  onChange={e => setLeadData(d => ({ ...d, email: e.target.value }))}
                  className="w-full px-4 py-3 bg-[var(--color-background)] border border-[var(--color-border)] rounded-xl text-[var(--color-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2">
                  <Phone className="w-4 h-4 inline mr-1" /> Phone
                </label>
                <input
                  type="tel"
                  value={leadData.phone}
                  onChange={e => setLeadData(d => ({ ...d, phone: e.target.value }))}
                  className="w-full px-4 py-3 bg-[var(--color-background)] border border-[var(--color-border)] rounded-xl text-[var(--color-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                  placeholder="+971 XX XXX XXXX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2">
                  <MessageCircle className="w-4 h-4 inline mr-1" /> WhatsApp
                </label>
                <input
                  type="tel"
                  value={leadData.whatsapp}
                  onChange={e => setLeadData(d => ({ ...d, whatsapp: e.target.value }))}
                  className="w-full px-4 py-3 bg-[var(--color-background)] border border-[var(--color-border)] rounded-xl text-[var(--color-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                  placeholder="+971 XX XXX XXXX"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2">
                  <Building2 className="w-4 h-4 inline mr-1" /> Company
                </label>
                <input
                  type="text"
                  value={leadData.company}
                  onChange={e => setLeadData(d => ({ ...d, company: e.target.value }))}
                  className="w-full px-4 py-3 bg-[var(--color-background)] border border-[var(--color-border)] rounded-xl text-[var(--color-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                  placeholder="Workshop name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2">
                  <MapPin className="w-4 h-4 inline mr-1" /> Country
                </label>
                <input
                  type="text"
                  value={leadData.country}
                  onChange={e => setLeadData(d => ({ ...d, country: e.target.value }))}
                  className="w-full px-4 py-3 bg-[var(--color-background)] border border-[var(--color-border)] rounded-xl text-[var(--color-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
                  placeholder="UAE / Saudi Arabia / ..."
                />
              </div>
            </div>
            <div className="mt-6 flex items-center gap-4 flex-wrap">
              <button
                onClick={submitLead}
                disabled={submitting || !leadData.email || !leadData.name}
                className="px-6 py-3 bg-[var(--color-primary)] text-[var(--color-background)] font-bold rounded-xl hover:bg-[var(--color-primary-dark)] transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {submitting ? 'Sending...' : 'Send Me the Full Report'}
                <Mail className="w-4 h-4" />
              </button>
              <button
                onClick={() => setShowLeadForm(false)}
                className="text-[var(--color-muted)] hover:text-[var(--color-foreground)] transition-colors text-sm"
              >
                Maybe later
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  const question = getCurrentQuestion();

  return (
    <div className="max-w-3xl mx-auto px-6 py-12">
      {/* Progress */}
      <div className="mb-12">
        <div className="flex justify-between mb-4">
          {steps.map(step => (
            <div key={step.id} className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                  currentStep > step.id
                    ? 'bg-[var(--color-primary)] text-[var(--color-background)]'
                    : currentStep === step.id
                    ? 'bg-[var(--color-primary)]/20 text-[var(--color-primary)] border-2 border-[var(--color-primary)]'
                    : 'bg-[var(--color-surface)] text-[var(--color-muted)] border border-[var(--color-border)]'
                }`}
              >
                {currentStep > step.id ? <CheckCircle2 className="w-5 h-5" /> : step.id}
              </div>
              <span className={`mt-2 text-xs font-medium ${
                currentStep >= step.id ? 'text-[var(--color-foreground)]' : 'text-[var(--color-muted)]'
              }`}>
                {step.label}
              </span>
            </div>
          ))}
        </div>
        <div className="h-1 bg-[var(--color-surface)] rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[var(--color-primary)] to-[var(--color-accent)] transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Question */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-sm text-[var(--color-primary)] font-semibold mb-3">
          <span className="w-6 h-6 rounded-full bg-[var(--color-primary)]/20 flex items-center justify-center text-xs">
            {currentStep}
          </span>
          Question {currentStep} of 5
        </div>
        <h1 className="text-2xl md:text-3xl font-bold text-[var(--color-foreground)] mb-2">
          {question.title}
        </h1>
        <p className="text-[var(--color-muted)]">{question.subtitle}</p>
      </div>

      {/* Options */}
      <div className="space-y-3 mb-10">
        {question.options?.map(option => {
          const isSelected = question.type === 'multiple'
            ? (answers as Record<string, string[]>)[question.id]?.includes(option.value)
            : (answers as Record<string, string>)[question.id] === option.value;

          return (
            <button
              key={option.value}
              onClick={() =>
                question.type === 'multiple'
                  ? handleMultiSelect(question.id, option.value)
                  : handleSingleSelect(question.id, option.value)
              }
              className={`w-full p-5 rounded-xl border text-left transition-all duration-300 ${
                isSelected
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary)]/10 shadow-[0_0_15px_rgba(0,255,136,0.15)]'
                  : 'border-[var(--color-border)] bg-[var(--color-surface)] hover:border-[var(--color-primary)]/50 hover:bg-[var(--color-surface-light)]'
              }`}
            >
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-[var(--color-background)] flex items-center justify-center text-xl">
                  {option.icon || <BatteryCharging className="w-5 h-5 text-[var(--color-muted)]" />}
                </div>
                <span className={`font-medium flex-1 ${
                  isSelected ? 'text-[var(--color-foreground)]' : 'text-[var(--color-muted)]'
                }`}>
                  {option.label}
                </span>
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  isSelected ? 'border-[var(--color-primary)] bg-[var(--color-primary)]' : 'border-[var(--color-border)]'
                }`}>
                  {isSelected && <CheckCircle2 className="w-3 h-3 text-[var(--color-background)]" />}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Vehicle info step */}
      {currentStep === 5 && (
        <div className="grid sm:grid-cols-2 gap-4 mb-10">
          <div>
            <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2">Vehicle Brand</label>
            <input
              type="text"
              value={(answers as Record<string, string>).vehicleBrand || ''}
              onChange={e => setAnswers(prev => ({ ...prev, vehicleBrand: e.target.value }))}
              className="w-full px-4 py-3 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl text-[var(--color-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
              placeholder="BYD / Tesla / Toyota..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2">Vehicle Model</label>
            <input
              type="text"
              value={(answers as Record<string, string>).vehicleModel || ''}
              onChange={e => setAnswers(prev => ({ ...prev, vehicleModel: e.target.value }))}
              className="w-full px-4 py-3 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl text-[var(--color-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
              placeholder="e.g. Han EV, Model 3..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2">Battery Capacity (kWh)</label>
            <input
              type="text"
              value={(answers as Record<string, string>).batteryCapacityKwh || ''}
              onChange={e => setAnswers(prev => ({ ...prev, batteryCapacityKwh: e.target.value }))}
              className="w-full px-4 py-3 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl text-[var(--color-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
              placeholder="e.g. 60, 77, 85"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--color-foreground)] mb-2">
              <Clock className="w-4 h-4 inline mr-1" /> Mileage (km)
            </label>
            <input
              type="text"
              value={(answers as Record<string, string>).mileageKm || ''}
              onChange={e => setAnswers(prev => ({ ...prev, mileageKm: e.target.value }))}
              className="w-full px-4 py-3 bg-[var(--color-surface)] border border-[var(--color-border)] rounded-xl text-[var(--color-foreground)] focus:outline-none focus:border-[var(--color-primary)] transition-colors"
              placeholder="e.g. 80,000"
            />
          </div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <button
          onClick={prevStep}
          disabled={currentStep === 1}
          className="flex items-center gap-2 px-5 py-3 border border-[var(--color-border)] rounded-xl text-[var(--color-foreground)] hover:border-[var(--color-primary)] hover:text-[var(--color-primary)] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="w-4 h-4" />
          Back
        </button>
        <button
          onClick={nextStep}
          disabled={!canProceed()}
          className="flex items-center gap-2 px-6 py-3 bg-[var(--color-primary)] text-[var(--color-background)] font-bold rounded-xl hover:bg-[var(--color-primary-dark)] transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_0_15px_rgba(0,255,136,0.25)] hover:shadow-[0_0_25px_rgba(0,255,136,0.4)]"
        >
          {currentStep === 5 ? 'Get Diagnosis' : 'Next'}
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
